/**
 * Consumes a magic-link token from the URL, exchanges it for a session,
 * and routes the user into the next phase of the app.
 *
 * Hosted at `/auth/magic-link?token=...&lang=...`. Both sign-up and login
 * land here — the API tells us via ``is_new_account`` whether this click
 * created the account, then AuthContext decides the next route.
 *
 * An expired / already-used link (401 `magic_link_invalid`) explains itself
 * and offers one action: email a new code to the address this browser last
 * requested a link for, or go back to sign-in. A network error offers Try
 * again. Someone already signed in here goes straight into the app
 * (AUTH-R30, decisions in `magicLinkConsume.ts`).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { MaterialIcons } from '@expo/vector-icons';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Text } from '@/components/ui/text';

import { authAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import { ROUTES } from '../../navigation/routes';
import { trackAuthEvent, trackMagicLinkConsumed } from '../../services/analytics';
import { analyticsErrorCode } from '../../services/analyticsPrivacy';
import { brand } from '../../design-system/theme/tokens';
import {
  classifyConsumeFailure,
  consumeRedirect,
  failureScreenShown,
  forgetSignInEmail,
  freshCodeLoginPath,
  rememberedSignInEmail,
  type ConsumeStatus,
} from './magicLinkConsume';

const COLORS = {
  primary: brand.primary,
  background: '#FFFFFF',
  surface: '#F9FAFB',
  border: '#E5E7EB',
  error: '#DC2626',
  success: '#10B981',
  text: { primary: '#111827', secondary: '#4B5563', muted: '#9CA3AF' },
};

export default function MagicLinkConsumeScreen() {
  const router = useRouter();
  const { t, language } = useTranslation();
  const { refreshAuth, phase } = useAuth();
  const params = useLocalSearchParams<{ token?: string }>();
  const [status, setStatus] = useState<ConsumeStatus>('verifying');
  const [sendingCode, setSendingCode] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const consumedTokenRef = useRef<string | null>(null);
  const [rememberedEmail] = useState<string | null>(() => rememberedSignInEmail());

  const consume = useCallback(async () => {
    const token = (Array.isArray(params.token) ? params.token[0] : params.token) || '';
    if (consumedTokenRef.current === token) {
      return;
    }
    if (!token) {
      setStatus('invalid');
      return;
    }
    consumedTokenRef.current = token;
    try {
      trackAuthEvent('magic_link_consume_attempted');
      const res = await authAPI.consumeMagicLink(token);
      trackAuthEvent('magic_link_consume_succeeded', { is_new_account: !!res?.is_new_account });
      trackMagicLinkConsumed(!!res?.is_new_account);
      if (res?.is_new_account) {
        trackAuthEvent('signup_completed', { method: 'magic_link' });
      }
      // Recompute the authenticated phase before exposing the success state;
      // otherwise the redirect effect can observe the old unauthenticated phase.
      await refreshAuth?.();
      forgetSignInEmail();
      setStatus('success');
    } catch (err) {
      const failure = classifyConsumeFailure(err);
      trackAuthEvent('magic_link_consume_failed', {
        error_code: analyticsErrorCode(err),
        reason: failure,
        has_remembered_email: !!rememberedEmail,
      });
      setStatus((current) => (current === 'success' ? current : failure));
    }
  }, [params.token, refreshAuth, rememberedEmail]);

  useEffect(() => {
    void consume();
  }, [consume]);

  useEffect(() => {
    const target = consumeRedirect(status, phase);
    if (!target) return;
    if (status !== 'success') {
      // The link did not work, but this browser is already signed in.
      trackAuthEvent('magic_link_consume_already_signed_in', { reason: status });
    }
    router.replace(target as any);
  }, [phase, router, status]);

  // Once per failed status: the screen the user is left on, and why.
  const shownFailureRef = useRef<ConsumeStatus | null>(null);
  useEffect(() => {
    if (!failureScreenShown(status, phase) || shownFailureRef.current === status) return;
    shownFailureRef.current = status;
    trackAuthEvent('magic_link_invalid_screen_shown', {
      reason: status,
      has_remembered_email: !!rememberedEmail,
    });
  }, [phase, rememberedEmail, status]);

  const retry = () => {
    consumedTokenRef.current = null;
    setStatus('verifying');
    void consume();
  };

  const sendNewCode = async () => {
    if (!rememberedEmail || sendingCode) return;
    setSendingCode(true);
    setNotice(null);
    try {
      trackAuthEvent('magic_link_invalid_new_code_requested');
      await authAPI.requestOtp(rememberedEmail, language);
      router.replace(freshCodeLoginPath(rememberedEmail, true) as any);
    } catch (err) {
      trackAuthEvent('magic_link_invalid_new_code_failed', { error_code: analyticsErrorCode(err) });
      setNotice(t('auth.passwordless.consume.newCodeFailed'));
      setSendingCode(false);
    }
  };

  const backToSignIn = () =>
    router.replace((rememberedEmail ? freshCodeLoginPath(rememberedEmail, false) : ROUTES.PUBLIC_LOGIN) as any);

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: COLORS.surface }}>
      <View className="flex-1 items-center justify-center px-6">
        <View style={{ width: '100%', maxWidth: 440 }}>
          <Card style={{ backgroundColor: COLORS.background, borderRadius: 16, borderWidth: 1, borderColor: COLORS.border }}>
            <CardContent style={{ padding: 32, alignItems: 'center' }}>
              {status === 'verifying' && (
                <>
                  <ActivityIndicator size="large" color={COLORS.primary} style={{ marginBottom: 16 }} />
                  <Text style={{ color: COLORS.text.primary, fontSize: 20, fontWeight: '700', marginBottom: 8 }}>
                    {t('auth.passwordless.consume.verifying')}
                  </Text>
                  <Text style={{ color: COLORS.text.secondary, fontSize: 14, textAlign: 'center' }}>
                    {t('auth.passwordless.consume.verifyingHint')}
                  </Text>
                </>
              )}

              {status === 'success' && (
                <>
                  <View
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 28,
                      backgroundColor: '#ECFDF5',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: 16,
                    }}
                  >
                    <MaterialIcons name="check-circle" size={32} color={COLORS.success} />
                  </View>
                  <Text style={{ color: COLORS.text.primary, fontSize: 20, fontWeight: '700', marginBottom: 8 }}>
                    {t('auth.passwordless.consume.success')}
                  </Text>
                  <Text style={{ color: COLORS.text.secondary, fontSize: 14, textAlign: 'center' }}>
                    {t('auth.passwordless.consume.successHint')}
                  </Text>
                </>
              )}

              {status === 'invalid' && (
                <>
                  <View
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 28,
                      backgroundColor: '#FEF2F2',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: 16,
                    }}
                  >
                    <MaterialIcons name="link-off" size={32} color={COLORS.error} />
                  </View>
                  <Text testID="magic-link-invalid-title" style={{ color: COLORS.text.primary, fontSize: 20, fontWeight: '700', marginBottom: 8, textAlign: 'center' }}>
                    {t('auth.passwordless.consume.invalid')}
                  </Text>
                  <Text style={{ color: COLORS.text.secondary, fontSize: 14, textAlign: 'center', marginBottom: 20 }}>
                    {rememberedEmail
                      ? t('auth.passwordless.consume.invalidHintWithEmail', { email: rememberedEmail })
                      : t('auth.passwordless.consume.invalidHint')}
                  </Text>
                  {notice && (
                    <Text style={{ color: COLORS.error, fontSize: 14, textAlign: 'center', marginBottom: 12 }}>
                      {notice}
                    </Text>
                  )}
                  {rememberedEmail ? (
                    <>
                      <Button
                        variant="default"
                        size="default"
                        onPress={() => void sendNewCode()}
                        disabled={sendingCode}
                        style={{ width: '100%' }}
                      >
                        <Text className="text-white font-semibold">
                          {sendingCode
                            ? t('auth.passwordless.consume.sendingNewCode')
                            : t('auth.passwordless.consume.sendNewCode')}
                        </Text>
                      </Button>
                      <Button
                        variant="outline"
                        size="default"
                        onPress={backToSignIn}
                        style={{ width: '100%', marginTop: 12 }}
                      >
                        <Text style={{ color: COLORS.text.primary, fontWeight: '600' }}>
                          {t('auth.passwordless.consume.backToSignIn')}
                        </Text>
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="default"
                      size="default"
                      onPress={backToSignIn}
                      style={{ width: '100%' }}
                    >
                      <Text className="text-white font-semibold">
                        {t('auth.passwordless.consume.backToSignIn')}
                      </Text>
                    </Button>
                  )}
                </>
              )}

              {status === 'error' && (
                <>
                  <View
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 28,
                      backgroundColor: '#FEF2F2',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: 16,
                    }}
                  >
                    <MaterialIcons name="error-outline" size={32} color={COLORS.error} />
                  </View>
                  <Text testID="magic-link-check-failed-title" style={{ color: COLORS.text.primary, fontSize: 20, fontWeight: '700', marginBottom: 8, textAlign: 'center' }}>
                    {t('auth.passwordless.consume.checkFailed')}
                  </Text>
                  <Text style={{ color: COLORS.text.secondary, fontSize: 14, textAlign: 'center', marginBottom: 20 }}>
                    {t('auth.passwordless.consume.checkFailedHint')}
                  </Text>
                  <Button variant="default" size="default" onPress={retry} style={{ width: '100%' }}>
                    <Text className="text-white font-semibold">
                      {t('auth.passwordless.consume.tryAgain')}
                    </Text>
                  </Button>
                  <Button
                    variant="outline"
                    size="default"
                    onPress={backToSignIn}
                    style={{ width: '100%', marginTop: 12 }}
                  >
                    <Text style={{ color: COLORS.text.primary, fontWeight: '600' }}>
                      {t('auth.passwordless.consume.backToSignIn')}
                    </Text>
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </View>
      </View>
    </SafeAreaView>
  );
}
